import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Head from 'next/head';
import App from '~/src/components/App';
import { withRouter } from 'next/router';
import { isClientSide } from '~/src/helpers/ssr';

const ymId = 30080914;

class Layout extends Component {
    static propTypes = {
        children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        description: PropTypes.string,
        image: PropTypes.string,
        siteName: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
        hostname: PropTypes.string,
        relCanonical: PropTypes.string,
        amphtml: PropTypes.string,
        linkedData: PropTypes.string,
        noindex: PropTypes.bool,
        isAuth: PropTypes.bool,
        clientPersonal: PropTypes.object,
    };

    static defaultProps = {
        title: 'Дом.ru',
        noindex: false,
    };

    constructor(props) {
        super(props);

        this.intervalId = null;
        this.isClientSide = isClientSide();
    }

    static async getInitialProps(ctx) {
        const isAMP = ctx.pathname.indexOf('_amp') !== -1;

        return {
            isAMP,
        };
    }

    componentDidMount() {
        this.setYMUserID();
    }

    setYMUserID = () => {
        const { isAuth, clientPersonal } = this.props;
        if (clientPersonal == null) {
            return;
        }
        const agreement = clientPersonal.agreement;
        if (!this.intervalId && isAuth && agreement) {
            this.intervalId = setInterval(() => {
                if (window.ym && window[`yaCounter${ymId}`]) {
                    window.ym(ymId, 'userParams', {
                        UserID: agreement,
                    });
                    clearInterval(this.intervalId);
                }
            }, 1000);
        }
    };

    componentDidUpdate() {
        process.browser && this.setYMUserID();
    }

    componentWillUnmount() {
        if (process.browser && this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    renderClientScripts = () => {
        if (!this.isClientSide) {
            return null;
        }

        return (
            <Fragment>
                <script async src='/static/js/googleTagManager.js'/>
                <script async src='/static/js/googleAnalytics.js'/>
                <script async src='/static/js/objectFitImages.js'/>
                <script async src='/static/js/facebookPixel.js'/>
                <script async src='/static/js/mailRuCounter.js'/>
            </Fragment>
        );
    };

    render() {
        const {
            amphtml,
            title,
            siteName,
            url,
            image,
            description,
            hostname,
            children,
            relCanonical,
            noindex = false,
            isAMP,
            router,
            linkedData,
            withMenu,
            ...props
        } = this.props;
        const { query: { page }, asPath } = router;

        let noIndex = null;
        if (noindex) {
            noIndex = <meta name='robots' content='noindex'/>;
        }

        if (router.asPath.indexOf('/news') !== -1 && page) {
            noIndex = <meta name='robots' content='noindex, follow'/>;
        }

        if (router.asPath.indexOf('/equipment/note') !== -1 || router.asPath.indexOf('/equipment/smart') !== -1 || router.asPath.indexOf('/zasmotris-village') !== -1  || router.asPath.indexOf('/bundles-land-new-2') !== -1) {
            noIndex = <meta name='robots' content='noindex, nofollow'/>;
        }

        let noyaca;
        if (router.pathname === ('/')) {
            noyaca = <meta name='robots' content='noyaca'/>;
        }

        let schemaAgentPage = null;
        let appAgentPage = null;
        if (router.asPath.indexOf('agent') !== -1) {
            appAgentPage = <meta name='apple-itunes-app' content='app-id=569251594'/>;
            schemaAgentPage = <Fragment>
                <script type={'application/ld+json'} dangerouslySetInnerHTML={{
                    __html: `
                    {
                        "@context": "https://schema.org",
                        "@type": "MobileApplication",
                        "name": "Мой Дом.ru",
                        "operatingSystem": "Android, iOS",
                        "applicationCategory": "https://schema.org/CommunicationApplication",
                        "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": "4.2",
                        "ratingCount": "7472"
                    },
                        "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "RUB"
                    },
                        "url": "https://play.google.com/store/apps/details?id=com.ertelecom.agent&hl=ru"
                    }
                `,
                }}/>
            </Fragment>;
        } else if (linkedData) {
            schemaAgentPage = <script type={'application/ld+json'} dangerouslySetInnerHTML={{ __html: linkedData }}/>;
        }

        return (
            <Fragment>
                <Head>
                    <title>{title}</title>
                    <script src='https://www.googleoptimize.com/optimize.js?id=OPT-MSHKM9J'/>
                    {this.renderClientScripts()}
                    <script>window.SC_DISABLE_SPEEDY = true</script>
                    <meta property='og:type' content='article'/>
                    <meta name='twitter:card' content='summary'/>
                    <meta name='twitter:creator' content='@domru_'/>
                    <meta name='twitter:site' content='@domru_'/>
                    <meta name='verify-reformal' content='06f45ba2cda3fd2ce81181b5'/>
                    <meta name='yandex-verification' content='36650e0e0c49aa3e' />
                    <meta name='theme-color' content='#000000'/>
                    <link rel='shortcut icon' href='/static/favicon.ico'/>
                    {
                        !isAMP &&
                        (<meta
                            name='viewport'
                            content='width=device-width, initial-scale=1, shrink-to-fit=no'
                        />)
                    }
                    <meta property='og:site_name' content={siteName || title}/>
                    <meta property='og:title' content={title}/>
                    <meta name='twitter:title' content={title}/>
                    <meta name='twitter:image' content={`https://${hostname}/images/logo.jpg`}/>
                    {noIndex}
                    {url && <meta property='og:url' content={url}/>}
                    {image && <meta property='og:image' content={image}/>}
                    {description &&
                        <Fragment>
                            <meta property='og:description' content={description}/>
                            <meta name='description' content={description}/>
                            <meta name='twitter:description' content={description}/>
                        </Fragment>
                    }
                    {relCanonical && <link rel='canonical' href={relCanonical}/>}
                    {amphtml && <link rel='amphtml' href={amphtml}/>}
                    {schemaAgentPage}
                    {appAgentPage}
                    {noyaca}
                </Head>
                <App
                    hostname={hostname}
                    asPath={asPath}
                    withMenu={withMenu}
                    {...props}
                >
                    {children}
                </App>
            </Fragment>
        );
    }
}

const mapStateToProps = state => ({
    isAuth: state.auth.isAuth,
    clientPersonal: state.auth.clientPersonal,
});

export default withRouter(connect(mapStateToProps)(Layout));
